import React, { useEffect, useState } from 'react';
import {
  createEscrow,
  fetchEscrows,
  type Escrow as EscrowT,
} from '../api/finance';
import { useAGUBalance, useSettlementTreasury, formatAGU, explorerUrl } from '../sui';
import { useCurrentAccount } from '@mysten/dapp-kit';

const TOKENS = ['AGU', 'USDC', 'SUI', 'ETH', 'ADA'];

export default function Escrow() {
  const [escrows, setEscrows] = useState<EscrowT[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<{ type: string; msg: string } | null>(null);

  const account = useCurrentAccount();
  const { balance } = useAGUBalance();
  const { info: treasury } = useSettlementTreasury();

  const [form, setForm] = useState({
    taskId: '',
    agentId: '',
    amount: '',
    token: 'AGU',
  });

  const load = async () => {
    try {
      const data = await fetchEscrows();
      setEscrows(data);
    } catch {
      /* keep current */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.taskId || !form.agentId || !form.amount) {
      setAlert({ type: 'error', msg: 'All fields are required.' });
      return;
    }
    setSubmitting(true);
    setAlert(null);
    try {
      await createEscrow({
        taskId: form.taskId,
        agentId: form.agentId,
        amount: Number(form.amount),
        token: form.token,
        status: 'locked',
      });
      setAlert({ type: 'success', msg: 'Escrow created successfully.' });
      setForm({ taskId: '', agentId: '', amount: '', token: 'AGU' });
      await load();
    } catch (e) {
      setAlert({ type: 'error', msg: String(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (s: string) => {
    const cls = s === 'locked' ? 'warning' : s === 'released' ? 'success' : 'info';
    return <span className={`finance-badge ${cls}`}>{s}</span>;
  };

  return (
    <div>
      <header className="finance-header">
        <h1>Escrow Management</h1>
        <p>Lock and release funds for AI agent task execution</p>
      </header>

      {/* ── On-chain Treasury Status ── */}
      <div className="finance-grid" style={{ marginBottom: 24 }}>
        <div className="finance-card" style={{ textAlign: 'center' }}>
          <div className="finance-stat-label">⛓️ Escrow Locked</div>
          <div className="finance-stat-value positive" style={{ fontSize: 24 }}>{formatAGU(treasury.totalLocked)} AGU</div>
          <div className="finance-stat-label">{treasury.escrowCount} active</div>
        </div>
        <div className="finance-card" style={{ textAlign: 'center' }}>
          <div className="finance-stat-label">✅ Total Settled</div>
          <div className="finance-stat-value" style={{ fontSize: 24 }}>{formatAGU(treasury.totalSettled)} AGU</div>
        </div>
        <div className="finance-card" style={{ textAlign: 'center' }}>
          <div className="finance-stat-label">💰 Your AGU Balance</div>
          <div className="finance-stat-value positive" style={{ fontSize: 24 }}>
            {account ? `${balance.toLocaleString(undefined, { maximumFractionDigits: 2 })} AGU` : 'Connect Wallet'}
          </div>
        </div>
      </div>

      {alert && (
        <div className={`finance-alert ${alert.type}`}>{alert.msg}</div>
      )}

      {/* ── Create Form ── */}
      <div className="finance-card" style={{ marginBottom: 24 }}>
        <div className="finance-card-header">
          <span className="finance-card-title">
            <span className="finance-icon">🔒</span>
            Create Escrow
          </span>
        </div>
        <form onSubmit={handleSubmit} className="finance-form">
          <div className="finance-form-row">
            <div className="finance-form-group">
              <label className="finance-label">Task ID</label>
              <input
                className="finance-input"
                placeholder="task-001"
                value={form.taskId}
                onChange={(e) => setForm({ ...form, taskId: e.target.value })}
              />
            </div>
            <div className="finance-form-group">
              <label className="finance-label">Agent ID</label>
              <input
                className="finance-input"
                placeholder="agent-alpha"
                value={form.agentId}
                onChange={(e) => setForm({ ...form, agentId: e.target.value })}
              />
            </div>
          </div>
          <div className="finance-form-row">
            <div className="finance-form-group">
              <label className="finance-label">Amount</label>
              <input
                className="finance-input"
                type="number"
                placeholder="1000"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </div>
            <div className="finance-form-group">
              <label className="finance-label">Token</label>
              <select
                className="finance-input"
                value={form.token}
                onChange={(e) => setForm({ ...form, token: e.target.value })}
              >
                {TOKENS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          <button className="finance-button" type="submit" disabled={submitting}>
            {submitting ? 'Creating…' : '🔒 Lock Escrow'}
          </button>
        </form>
      </div>

      {/* ── Escrow List ── */}
      <div className="finance-card">
        <div className="finance-card-header">
          <span className="finance-card-title">
            <span className="finance-icon">📋</span>
            All Escrows
          </span>
          <span className="finance-badge info">{escrows.length}</span>
        </div>
        {loading ? (
          <div className="finance-loading">Loading…</div>
        ) : escrows.length === 0 ? (
          <div className="finance-empty">No escrows found. Create one above.</div>
        ) : (
          <div className="finance-table-wrap">
            <table className="finance-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Agent</th>
                  <th>Amount</th>
                  <th>Token</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {escrows.map((e, i) => (
                  <tr key={e.id || i}>
                    <td className="mono">{e.taskId}</td>
                    <td className="mono">{e.agentId}</td>
                    <td>{e.amount}</td>
                    <td>{e.token}</td>
                    <td>{statusBadge(e.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
